import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { TrendingUp, DollarSign, Home, Building2, Car, Warehouse } from"lucide-react";
import { toast } from"sonner";

const PotentialSalesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("all");
 const [units, setUnits] = useState([]);
 const [pricing, setPricing] = useState({});
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [selectedUnit, setSelectedUnit] = useState(null);
 const [pricePerM2, setPricePerM2] = useState(0);

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (projects.length > 0) {
 loadData();
 }
 }, [selectedProject, projects]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 }
 };

 const getSelectedProjectIds = () => {
 if (selectedProject ==="all") return projects.map(p => p.id);
 return [selectedProject];
 };

 const loadData = async () => {
 setLoading(true);
 try {
 const projectIds = getSelectedProjectIds();
 const [unitsRes, buildingsRes] = await Promise.all([
 axios.get("/units"),
 axios.get("/buildings")
 ]);
 
 const projectBuildings = buildingsRes.data.filter(b => projectIds.includes(b.project_id));
 const buildingIds = projectBuildings.map(b => b.id);
 
 const pricingPromises = projectIds.map(pid => 
 axios.get(`/unit-pricing/${pid}`).catch(() => ({ data: [] }))
 );
 const rentalsPromises = projectIds.map(pid =>
 axios.get(`/unit-rentals/${pid}`).catch(() => ({ data: [] }))
 );
 
 const [pricingResults, rentalsResults] = await Promise.all([
 Promise.all(pricingPromises),
 Promise.all(rentalsPromises)
 ]);
 
 const allPricing = pricingResults.flatMap(r => r.data);
 const allRentals = rentalsResults.flatMap(r => r.data);
 const rentalUnitIds = allRentals.map(r => r.unit_id);
 
 const unsoldUnits = unitsRes.data.filter(u => 
 buildingIds.includes(u.building_id) &&
 u.status !== 'sold' &&
 !rentalUnitIds.includes(u.id)
 );
 
 setUnits(unsoldUnits);
 
 const pricingMap = {};
 allPricing.forEach(p => { pricingMap[p.unit_id] = p; });
 setPricing(pricingMap);
 } catch (error) {
 console.error("Error loading data", error);
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const openPricingDialog = (unit) => {
 setSelectedUnit(unit);
 setPricePerM2(pricing[unit.id]?.price_per_m2 || 0);
 setDialogOpen(true);
 };

 const savePricing = async () => {
 try {
 const buildingsRes = await axios.get("/buildings");
 const building = buildingsRes.data.find(b => b.id === selectedUnit.building_id);
 const projectId = building?.project_id;
 if (!projectId) { toast.error(t("messages.loadFailed")); return; }
 
 await axios.post("/unit-pricing", {
 unit_id: selectedUnit.id,
 project_id: projectId,
 price_per_m2: pricePerM2,
 currency:"EUR"
 });
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 loadData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const setBulkPrice = async (unitType, price) => {
 const unitsToUpdate = units.filter(u => u.unit_type === unitType);
 if (unitsToUpdate.length === 0) { toast.error(`No ${unitType} units to update`); return; }

 const label = unitType === 'parking' || unitType === 'bicycle_parking' ? 'parking' : unitType;
 if (!window.confirm(`Set ${formatCurrency(price)}/m² for all ${unitsToUpdate.length} ${label} units?`)) return;

 try {
 toast.info(`Updating ${unitsToUpdate.length} units...`);
 const buildingsRes = await axios.get("/buildings");
 const buildings = buildingsRes.data;
 
 await Promise.all(
 unitsToUpdate.map(unit => {
 const building = buildings.find(b => b.id === unit.building_id);
 return axios.post("/unit-pricing", {
 unit_id: unit.id,
 project_id: building?.project_id || getSelectedProjectIds()[0],
 price_per_m2: price,
 currency:"EUR"
 });
 })
 );

 toast.success(`Updated pricing for ${unitsToUpdate.length} ${label} units`);
 loadData();
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency', currency: 'EUR', minimumFractionDigits: 0
 }).format(amount || 0);
 };

 // Calculate statistics by type
 const calcStats = (typeFilter) => {
 const filtered = units.filter(typeFilter);
 const totalM2 = filtered.reduce((sum, u) => sum + (u.net_m2 || 0), 0);
 const potential = filtered.reduce((sum, u) => sum + (u.net_m2 * (pricing[u.id]?.price_per_m2 || 0)), 0);
 return { units: filtered, count: filtered.length, totalM2, potential };
 };

 const res = calcStats(u => u.unit_type === 'residential');
 const com = calcStats(u => u.unit_type === 'commercial');
 const pkg = calcStats(u => u.unit_type === 'parking' || u.unit_type === 'bicycle_parking');
 const stg = calcStats(u => u.unit_type === 'storage');
 const totalPotential = res.potential + com.potential + pkg.potential + stg.potential;

 const typeConfig = {
 residential: { color: 'purple', icon: Home, label: t('potentialSales.residentialPotential') },
 commercial: { color: 'blue', icon: Building2, label: t('potentialSales.commercialPotential') },
 parking: { color: 'amber', icon: Car, label: t('potentialSales.parkingPotential') },
 storage: { color: 'stone', icon: Warehouse, label: t('potentialSales.storagePotential') },
 };

 const SummaryCard = ({ stats, type, bulkId }) => {
 const cfg = typeConfig[type];
 const Icon = cfg.icon;
 return (
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3 mb-2">
 <Icon className={`h-6 w-6 text-${cfg.color}-600${cfg.color}-400`} />
 <div className="text-sm text-slate-500 font-semibold">{cfg.label}</div>
 </div>
 <div className="text-3xl font-bold text-slate-900">{formatCurrency(stats.potential)}</div>
 <div className="text-xs text-slate-600 mt-1">{stats.totalM2.toFixed(0)} m² - {stats.count} {t('common.units')}</div>
 {stats.count > 0 && (
 <div className="mt-3 pt-3 border-t border-slate-200">
 <div className="flex gap-2 items-center">
 <Input type="number"step="0.01"placeholder={`${formatCurrency(0)}/m²`} className="h-8 text-sm"id={bulkId} />
 <Button size="sm"variant="outline"className="h-8 text-xs"onClick={() => {
 const input = document.getElementById(bulkId);
 const price = parseFloat(input.value);
 if (price > 0) {
 if (type === 'parking') {
 setBulkPrice('parking', price).then(() => setBulkPrice('bicycle_parking', price));
 } else {
 setBulkPrice(type, price);
 }
 } else { toast.error(t('validation.invalidNumber')); }
 }}>{t('common.apply')}</Button>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 );
 };

 const getTypeBadge = (unitType) => {
 const styles = {
 residential: 'bg-purple-100 text-purple-700',
 commercial: 'bg-blue-500/10 text-blue-700',
 parking: 'bg-amber-100 text-amber-700',
 bicycle_parking: 'bg-amber-100 text-amber-700',
 storage: 'bg-stone-200 text-stone-700',
 };
 return styles[unitType] || 'bg-white/60 text-[#334155]';
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t('potentialSales.title')}</h1>
 <p className="text-slate-500 mt-1">{t('potentialSales.subtitle')}</p>
 </div>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64"data-testid="project-select-potential">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">{t('common.allProjects')}</SelectItem>
 {projects.map(p => (
 <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
 <SummaryCard stats={res} type="residential"bulkId="bulk-res"/>
 <SummaryCard stats={com} type="commercial"bulkId="bulk-com"/>
 <SummaryCard stats={pkg} type="parking"bulkId="bulk-pkg"/>
 <SummaryCard stats={stg} type="storage"bulkId="bulk-stg"/>
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3 mb-2">
 <TrendingUp className="h-6 w-6 text-emerald-600"/>
 <div className="text-sm text-slate-500 font-semibold">{t('potentialSales.totalPotential')}</div>
 </div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{formatCurrency(totalPotential)}</div>
 <div className="text-xs text-slate-600 mt-1">{units.length} {t('common.units')}</div>
 </CardContent>
 </Card>
 </div>

 {/* Units List */}
 <Card>
 <CardHeader>
 <CardTitle>{t('potentialSales.unsoldUnits')}</CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-12">{t('common.loading')}</div>
 ) : units.length === 0 ? (
 <div className="text-center py-12 text-slate-500">{t('potentialSales.noUnsoldUnits')}</div>
 ) : (
 <div className="space-y-2">
 {units.map((unit) => {
 const unitPricing = pricing[unit.id];
 const ppm2 = unitPricing?.price_per_m2 || 0;
 const totalPrice = unit.net_m2 * ppm2;
 
 return (
 <div key={unit.id} className="border rounded-sm p-4 hover:bg-white/80">
 <div className="flex items-center justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3">
 <h3 className="font-semibold text-slate-900">{t('units.unit')} {unit.code}</h3>
 <span className={`px-2 py-1 rounded text-xs ${getTypeBadge(unit.unit_type)}`}>
 {unit.unit_type === 'bicycle_parking' ? 'parking' : unit.unit_type}
 </span>
 </div>
 <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
 <div>
 <span className="text-sm text-slate-500">{t('units.area')}: </span>
 <span className="font-semibold">{unit.net_m2} m²</span>
 </div>
 <div>
 <span className="text-sm text-slate-500">{t('potentialSales.pricePerM2')}: </span>
 <span className="font-semibold text-slate-900">{ppm2 > 0 ? formatCurrency(ppm2) : t('potentialSales.notSet')}</span>
 </div>
 <div>
 <span className="text-sm text-slate-500">{t('common.total')}: </span>
 <span className="font-bold text-slate-900">{totalPrice > 0 ? formatCurrency(totalPrice) : '-'}</span>
 </div>
 </div>
 </div>
 <Button onClick={() => openPricingDialog(unit)}>
 <DollarSign className="w-4 h-4 mr-1"/>
 {t('potentialSales.setPrice')}
 </Button>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Pricing Dialog */}
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>{t('potentialSales.setPrice')} - {t('units.unit')} {selectedUnit?.code}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div className="bg-white/50 p-3 rounded">
 <div className="text-sm text-slate-500">{t('units.area')}</div>
 <div className="text-xl font-bold">{selectedUnit?.net_m2} m²</div>
 </div>
 <div>
 <Label htmlFor="price_per_m2">{t('potentialSales.pricePerM2')} (EUR)</Label>
 <Input
 id="price_per_m2"
 type="number"
 step="0.01"
 value={pricePerM2}
 onChange={(e) => setPricePerM2(parseFloat(e.target.value))}
 />
 </div>
 <div className="bg-white/50 p-3 rounded border border-slate-200">
 <div className="text-sm text-slate-500 font-semibold">{t('potentialSales.totalPrice')}</div>
 <div className="text-2xl font-bold text-slate-900">
 {formatCurrency((selectedUnit?.net_m2 || 0) * pricePerM2)}
 </div>
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button onClick={savePricing} className="flex-1">{t('common.save')}</Button>
 <Button variant="outline"onClick={() => setDialogOpen(false)} className="flex-1">{t('common.cancel')}</Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default PotentialSalesPage;
